import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Security, Warning, CheckCircle, Error } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface SecurityTestItem {
  name: string;
  description: string;
  status: 'pending' | 'success' | 'error' | 'warning';
  details?: string;
  timestamp?: Date;
}

interface SecurityReport {
  totalTests: number;
  passed: number;
  failed: number;
  warnings: number;
  score: number;
  recommendations: string[];
}

const SecurityTest: React.FC = () => {
  const { user, logout: _logout } = useAuth();
  const [tests, setTests] = useState<SecurityTestItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [report, setReport] = useState<SecurityReport>({
    totalTests: 0,
    passed: 0,
    failed: 0,
    warnings: 0,
    score: 0,
    recommendations: []
  });
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [_currentToken, _setCurrentToken] = useState('');

  const securityTests: Omit<SecurityTestItem, 'status' | 'timestamp'>[] = [
    {
      name: '🔐 JWT Token Validation',
      description: 'Проверка валидности JWT токена и его структуры'
    },
    {
      name: '🔑 Token Expiration',
      description: 'Проверка срока действия токена'
    },
    {
      name: '🛡️ Authorization Headers',
      description: 'Проверка корректности заголовков авторизации'
    },
    {
      name: '🚫 Unauthorized Access',
      description: 'Тестирование доступа к защищенным endpoints без токена'
    },
    {
      name: '👤 User Role Validation',
      description: 'Проверка валидации ролей пользователя'
    },
    {
      name: '🔒 CORS Configuration',
      description: 'Проверка настроек CORS'
    },
    {
      name: '📝 Content Security Policy',
      description: 'Проверка настроек CSP'
    },
    {
      name: '🔄 Token Refresh',
      description: 'Тестирование механизма обновления токенов'
    },
    {
      name: '🚪 Session Management',
      description: 'Проверка управления сессиями'
    },
    {
      name: '🔍 API Rate Limiting',
      description: 'Проверка ограничений на количество запросов'
    }
  ];

  const initializeTests = () => {
    setTests(securityTests.map(test => ({
      ...test,
      status: 'pending'
    })));
  };

  const updateTest = (testName: string, status: 'success' | 'error' | 'warning', details?: string) => {
    setTests(prev => prev.map(test => 
      test.name === testName 
        ? { ...test, status, timestamp: new Date(), ...(details !== undefined ? { details } : {}) } as SecurityTestItem
        : test
    ));
  };

  const testJWTTokenValidation = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        updateTest('🔐 JWT Token Validation', 'error', 'Токен не найден в localStorage');
        return;
      }

      // Проверяем структуру JWT токена
      const parts = token.split('.');
      if (parts.length !== 3) {
        updateTest('🔐 JWT Token Validation', 'error', 'Неверная структура JWT токена');
        return;
      }

      // Декодируем payload
      try {
        const part1 = parts[1];
        if (!part1) {
          updateTest('🔐 JWT Token Validation', 'error', 'Неверная структура JWT токена');
          return;
        }
        const payload = JSON.parse(atob(part1));
        if (!payload.exp || !payload.sub) {
          updateTest('🔐 JWT Token Validation', 'warning', 'JWT токен не содержит обязательных полей');
          return;
        }
        updateTest('🔐 JWT Token Validation', 'success', 'JWT токен валиден');
      } catch (e) {
        updateTest('🔐 JWT Token Validation', 'error', 'Не удалось декодировать JWT payload');
      }
    } catch (error) {
      updateTest('🔐 JWT Token Validation', 'error', 'Ошибка при проверке JWT токена');
    }
  };

  const testTokenExpiration = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        updateTest('🔑 Token Expiration', 'error', 'Токен не найден');
        return;
      }

      const part1 = token.split('.')[1];
      if (!part1) {
        updateTest('🔑 Token Expiration', 'error', 'Неверная структура токена');
        return;
      }
      const payload = JSON.parse(atob(part1));
      const expirationTime = payload.exp * 1000; // конвертируем в миллисекунды
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;

      if (timeUntilExpiration <= 0) {
        updateTest('🔑 Token Expiration', 'error', 'Токен истек');
      } else if (timeUntilExpiration < 5 * 60 * 1000) { // менее 5 минут
        updateTest('🔑 Token Expiration', 'warning', `Токен истекает через ${Math.round(timeUntilExpiration / 1000 / 60)} минут`);
      } else {
        updateTest('🔑 Token Expiration', 'success', `Токен действителен еще ${Math.round(timeUntilExpiration / 1000 / 60)} минут`);
      }
    } catch (error) {
      updateTest('🔑 Token Expiration', 'error', 'Ошибка при проверке срока действия токена');
    }
  };

  const testAuthorizationHeaders = async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.status === 200) {
        updateTest('🛡️ Authorization Headers', 'success', 'Заголовки авторизации работают корректно');
      } else {
        updateTest('🛡️ Authorization Headers', 'warning', 'Неожиданный статус ответа');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        updateTest('🛡️ Authorization Headers', 'error', 'Ошибка авторизации - неверные заголовки');
      } else {
        updateTest('🛡️ Authorization Headers', 'error', 'Ошибка при проверке заголовков авторизации');
      }
    }
  };

  const testUnauthorizedAccess = async () => {
    try {
      // Создаем временный API клиент без токена
      const tempApi = api.create({
        baseURL: process.env['REACT_APP_API_BASE_URL'] || 'http://localhost:3001/api',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Удаляем заголовок авторизации
      delete tempApi.defaults.headers.common['Authorization'];

      await tempApi.get('/analytics/dashboards');
      updateTest('🚫 Unauthorized Access', 'error', 'Доступ к защищенному endpoint без авторизации');
    } catch (error: any) {
      if (error.response?.status === 401) {
        updateTest('🚫 Unauthorized Access', 'success', 'Защищенный endpoint корректно отклоняет неавторизованные запросы');
      } else {
        updateTest('🚫 Unauthorized Access', 'warning', 'Неожиданная ошибка при тестировании неавторизованного доступа');
      }
    }
  };

  const testUserRoleValidation = async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data && response.data.role) {
        updateTest('👤 User Role Validation', 'success', `Роль пользователя: ${response.data.role}`);
      } else {
        updateTest('👤 User Role Validation', 'warning', 'Роль пользователя не определена');
      }
    } catch (error) {
      updateTest('👤 User Role Validation', 'error', 'Ошибка при проверке роли пользователя');
    }
  };

  const testCORSConfiguration = async () => {
    try {
      const response = await fetch(`${process.env['REACT_APP_API_BASE_URL'] || 'http://localhost:3001/api'}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        updateTest('🔒 CORS Configuration', 'success', 'CORS настроен корректно');
      } else {
        updateTest('🔒 CORS Configuration', 'warning', 'CORS может быть настроен некорректно');
      }
    } catch (error) {
      updateTest('🔒 CORS Configuration', 'error', 'Ошибка CORS при запросе');
    }
  };

  const testContentSecurityPolicy = async () => {
    try {
      // Проверяем CSP заголовки
      const response = await fetch(window.location.href);
      const cspHeader = response.headers.get('content-security-policy');
      
      if (cspHeader) {
        updateTest('📝 Content Security Policy', 'success', 'CSP заголовки присутствуют');
      } else {
        updateTest('📝 Content Security Policy', 'warning', 'CSP заголовки отсутствуют');
      }
    } catch (error) {
      updateTest('📝 Content Security Policy', 'error', 'Ошибка при проверке CSP');
    }
  };

  const testTokenRefresh = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        updateTest('🔄 Token Refresh', 'warning', 'Refresh токен не найден');
        return;
      }

      // Симулируем запрос на обновление токена
      const response = await api.post('/auth/refresh', {
        refreshToken
      });

      if (response.status === 200) {
        updateTest('🔄 Token Refresh', 'success', 'Механизм обновления токенов работает');
      } else {
        updateTest('🔄 Token Refresh', 'warning', 'Неожиданный ответ при обновлении токена');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        updateTest('🔄 Token Refresh', 'warning', 'Refresh токен недействителен');
      } else {
        updateTest('🔄 Token Refresh', 'error', 'Ошибка при тестировании обновления токена');
      }
    }
  };

  const testSessionManagement = async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data && response.data.id) {
        updateTest('🚪 Session Management', 'success', 'Сессия активна и валидна');
      } else {
        updateTest('🚪 Session Management', 'warning', 'Сессия может быть некорректной');
      }
    } catch (error) {
      updateTest('🚪 Session Management', 'error', 'Ошибка при проверке сессии');
    }
  };

  const testAPIRateLimiting = async () => {
    try {
      // Делаем несколько быстрых запросов для проверки rate limiting
      const promises = Array(10).fill(null).map(() => api.get('/health'));
      const responses = await Promise.all(promises);
      
      const successCount = responses.filter(r => r.status === 200).length;
      if (successCount === responses.length) {
        updateTest('🔍 API Rate Limiting', 'warning', 'Rate limiting может быть не настроен');
      } else {
        updateTest('🔍 API Rate Limiting', 'success', 'Rate limiting работает');
      }
    } catch (error: any) {
      if (error.response?.status === 429) {
        updateTest('🔍 API Rate Limiting', 'success', 'Rate limiting активен');
      } else {
        updateTest('🔍 API Rate Limiting', 'warning', 'Не удалось определить настройки rate limiting');
      }
    }
  };

  const runAllSecurityTests = async () => {
    setIsLoading(true);
    initializeTests();

    // Запускаем все тесты последовательно
    await testJWTTokenValidation();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testTokenExpiration();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testAuthorizationHeaders();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testUnauthorizedAccess();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testUserRoleValidation();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testCORSConfiguration();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testContentSecurityPolicy();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testTokenRefresh();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testSessionManagement();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    await testAPIRateLimiting();

    setIsLoading(false);
    calculateSecurityReport();
  };

  const calculateSecurityReport = () => {
    const totalTests = tests.length;
    const passed = tests.filter(t => t.status === 'success').length;
    const failed = tests.filter(t => t.status === 'error').length;
    const warnings = tests.filter(t => t.status === 'warning').length;
    const score = Math.round((passed / totalTests) * 100);

    const recommendations: string[] = [];
    if (failed > 0) {
      recommendations.push('❌ Исправьте критические ошибки безопасности');
    }
    if (warnings > 0) {
      recommendations.push('⚠️ Обратите внимание на предупреждения безопасности');
    }
    if (score < 80) {
      recommendations.push('🔒 Рекомендуется улучшить общую безопасность системы');
    }
    if (score >= 90) {
      recommendations.push('✅ Отличный уровень безопасности');
    }

    setReport({
      totalTests,
      passed,
      failed,
      warnings,
      score,
      recommendations
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle color="success" />;
      case 'error': return <Error color="error" />;
      case 'warning': return <Warning color="warning" />;
      default: return <CircularProgress size={16} />;
    }
  };

  const getSecurityScoreColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  return (
    <Card sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        🔒 Тестирование безопасности системы
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Пользователь: {user?.email}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Backend URL: {process.env['REACT_APP_API_BASE_URL'] || 'http://localhost:3001/api'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Всего тестов безопасности: {securityTests.length}
        </Typography>
      </Box>

      {/* Отчет о безопасности */}
      {report.totalTests > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            📊 Отчет о безопасности
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Security color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color={getSecurityScoreColor(report.score)}>
                  {report.score}%
                </Typography>
                <Typography variant="body2">Общий балл безопасности</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="success.main">
                  {report.passed}
                </Typography>
                <Typography variant="body2">Пройдено тестов</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Error color="error" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="error.main">
                  {report.failed}
                </Typography>
                <Typography variant="body2">Ошибки</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Warning color="warning" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {report.warnings}
                </Typography>
                <Typography variant="body2">Предупреждения</Typography>
              </Box>
            </Grid>
          </Grid>

          {report.recommendations.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                💡 Рекомендации
              </Typography>
              <List dense>
                {report.recommendations.map((rec, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={rec} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      )}

      <Box sx={{ mb: 3 }}>
        <Button 
          variant="contained" 
          size="large"
          onClick={runAllSecurityTests}
          disabled={isLoading}
          sx={{ mr: 2 }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Тестирование безопасности...
            </>
          ) : (
            '🔒 Запустить тесты безопасности'
          )}
        </Button>
        <Button 
          variant="outlined" 
          size="large"
          onClick={() => setShowTokenDialog(true)}
        >
          🔍 Просмотреть токен
        </Button>
      </Box>

      {/* Результаты тестов */}
      {tests.length > 0 && (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Тест безопасности</TableCell>
                <TableCell>Описание</TableCell>
                <TableCell align="center">Статус</TableCell>
                <TableCell>Детали</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tests.map((test) => (
                <TableRow key={test.name}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {test.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {test.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {getStatusIcon(test.status)}
                  </TableCell>
                  <TableCell>
                    {test.details && (
                      <Typography variant="body2" color="text.secondary">
                        {test.details}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Диалог для просмотра токена */}
      <Dialog open={showTokenDialog} onClose={() => setShowTokenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>🔍 JWT Token Analysis</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={localStorage.getItem('access_token') || 'Токен не найден'}
            variant="outlined"
            label="JWT Token"
            sx={{ mb: 2 }}
          />
          {localStorage.getItem('access_token') && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Decoded Payload:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={(() => {
                  try {
                    const token = localStorage.getItem('access_token');
                    if (token) {
                      const part1 = token.split('.')[1];
                      if (part1) {
                        const payload = JSON.parse(atob(part1));
                        return JSON.stringify(payload, null, 2);
                      }
                    }
                    return 'Не удалось декодировать токен';
                  } catch (e) {
                    return 'Ошибка декодирования токена';
                  }
                })()}
                variant="outlined"
                label="Decoded Payload"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTokenDialog(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {tests.length === 0 && !isLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Нажмите кнопку для начала тестирования безопасности системы
        </Typography>
      )}
    </Card>
  );
};

export default SecurityTest;
